import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import type { ScrapperEventListItem, ScrapperEventStatus, ScrapperEventType, ScrapperEventsListResult } from './types';
import { EVENT_TYPE_LABELS, STATUS_LABELS } from './types';
import ScrapperEventDetailDrawer from './ScrapperEventDetailDrawer';

interface Props {
  refreshKey: number;
  onRefresh: () => void;
  initialStatus: ScrapperEventStatus | 'all';
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Santiago',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const STATUS_TONE: Record<ScrapperEventStatus, { ring: string; bg: string; text: string; bar: string; dot: string; label: string }> = {
  pending: { ring: 'ring-amber-200 dark:ring-amber-900/50', bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-300', bar: 'bg-amber-400', dot: 'bg-amber-500', label: 'PENDIENTE' },
  processing: { ring: 'ring-cyan-200 dark:ring-cyan-900/50', bg: 'bg-cyan-50 dark:bg-cyan-950/30', text: 'text-cyan-700 dark:text-cyan-300', bar: 'bg-cyan-400', dot: 'bg-cyan-500', label: 'PROCESANDO' },
  completed: { ring: 'ring-emerald-200 dark:ring-emerald-900/50', bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-300', bar: 'bg-emerald-400', dot: 'bg-emerald-500', label: 'OK' },
  failed: { ring: 'ring-rose-200 dark:ring-rose-900/50', bg: 'bg-rose-50 dark:bg-rose-950/30', text: 'text-rose-700 dark:text-rose-300', bar: 'bg-rose-500', dot: 'bg-rose-500', label: 'FALLIDO' },
};

const ALL_STATUSES: (ScrapperEventStatus | 'all')[] = ['all', 'failed', 'pending', 'processing', 'completed'];
const ALL_TYPES: (ScrapperEventType | 'all')[] = ['all', 'daily', 'historical', 'accounts_by_code'];

export default function ScrapperEventsTable({ refreshKey, onRefresh, initialStatus }: Props) {
  const [status, setStatus] = useState<ScrapperEventStatus | 'all'>(initialStatus);
  const [eventType, setEventType] = useState<ScrapperEventType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [data, setData] = useState<ScrapperEventsListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ScrapperEventListItem | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  // Sincronizar cuando se filtra desde fuera (ej: click en "Fallidos")
  useEffect(() => {
    setStatus(initialStatus);
    setPage(1);
  }, [initialStatus]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const base = getAdminEndpointUrl('scrapperEvents', 'list');
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      if (eventType !== 'all') params.set('eventType', eventType);
      if (search.trim()) params.set('search', search.trim());
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      const url = `${base}?${params.toString()}`;
      const result = await adminHttpClient.get<ScrapperEventsListResult>(url);
      setData(result);
    } catch (err) {
      console.error('Error cargando eventos del scrapper', err);
      setData({ page, pageSize, total: 0, items: [] });
    } finally {
      setLoading(false);
    }
  }, [status, eventType, search, page, pageSize]);

  useEffect(() => {
    void fetchList();
  }, [fetchList, refreshKey]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleClearFilters = () => {
    setStatus('all');
    setEventType('all');
    setSearch('');
    setSearchInput('');
    setPage(1);
  };

  const handleRetry = async (id: string) => {
    setRetrying(id);
    try {
      await adminHttpClient.post(getAdminEndpointUrl('scrapperEvents', 'retry', { id }));
      onRefresh();
    } catch (err) {
      console.error('Error reintentando evento', err);
      const message = err instanceof Error ? err.message : 'Error reintentando evento';
      alert(message);
    } finally {
      setRetrying(null);
    }
  };

  const totalPages = useMemo(() => (data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1), [data]);

  return (
    <>
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              {ALL_STATUSES.map((opt) => {
                const isActive = status === opt;
                const tone = opt === 'all' ? null : STATUS_TONE[opt as ScrapperEventStatus];
                return (
                  <button
                    key={opt}
                    onClick={() => {
                      setStatus(opt);
                      setPage(1);
                    }}
                    className={`group inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-all ${
                      isActive
                        ? tone
                          ? `${tone.bg} ${tone.text} ring-1 ring-inset ${tone.ring} shadow-sm`
                          : 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                        : 'border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800'
                    }`}
                  >
                    {tone && <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />}
                    {opt === 'all' ? 'Todos' : STATUS_LABELS[opt as ScrapperEventStatus]}
                  </button>
                );
              })}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <select
                value={eventType}
                onChange={(e) => {
                  setEventType(e.target.value as typeof eventType);
                  setPage(1);
                }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 transition-colors focus:border-slate-400 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              >
                {ALL_TYPES.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === 'all' ? 'Todos los tipos' : EVENT_TYPE_LABELS[opt as ScrapperEventType]}
                  </option>
                ))}
              </select>

              <button
                onClick={onRefresh}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 transition-all hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                <span className="material-symbols-outlined text-[14px]">refresh</span>
                Refrescar
              </button>
            </div>
          </div>

          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-slate-400">search</span>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar por error code, scrapper id, file name o id…"
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 font-mono text-xs text-slate-700 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              />
            </div>
            <button type="submit" className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
              Buscar
            </button>
            {(status !== 'all' || eventType !== 'all' || search) && (
              <button type="button" onClick={handleClearFilters} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                limpiar
              </button>
            )}
          </form>
        </div>

        {/* Tabla */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:border-slate-800 dark:bg-slate-950/40">
                <th className="w-1 py-3" />
                <th className="py-3 pl-3 pr-4">Estado</th>
                <th className="py-3 px-4">Tipo / Adquirente</th>
                <th className="py-3 px-4">Comercio</th>
                <th className="py-3 px-4">Archivo</th>
                <th className="py-3 px-4">Error</th>
                <th className="py-3 px-4 text-right">Intentos</th>
                <th className="py-3 px-4">Recibido</th>
                <th className="py-3 px-4 text-right pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="py-12 text-center">
                    <span className="inline-flex items-center gap-2 font-mono text-xs text-slate-400">
                      <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>
                      cargando eventos…
                    </span>
                  </td>
                </tr>
              )}

              {!loading && data && data.items.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <span className="material-symbols-outlined text-3xl">inbox</span>
                      <p className="font-mono text-xs">Sin eventos con los filtros actuales</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                data?.items.map((row) => {
                  const tone = STATUS_TONE[row.status];
                  const isProcessing = row.status === 'processing';
                  return (
                    <tr
                      key={row.id}
                      className="group cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/60 dark:border-slate-800 dark:hover:bg-slate-800/40"
                      onClick={() => setSelected(row)}
                    >
                      <td className="relative">
                        <div className={`absolute left-0 top-1/2 h-10 w-1 -translate-y-1/2 rounded-r-full ${tone.bar} ${isProcessing ? 'animate-pulse' : ''}`} />
                      </td>
                      <td className="py-3 pl-3 pr-4">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tone.bg} ${tone.text} ring-1 ring-inset ${tone.ring}`}>
                          {isProcessing ? <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" /><span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${tone.dot}`} /></span> : <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />}
                          {tone.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-xs font-medium text-slate-800 dark:text-slate-100">{EVENT_TYPE_LABELS[row.eventType]}</p>
                        <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{row.acquirer.slug || row.acquirer.id}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-xs text-slate-700 dark:text-slate-200 truncate max-w-[180px]">{row.company.businessName ?? '—'}</p>
                        <p className="font-mono text-[10px] text-slate-400">{row.company.taxId ?? row.company.id}</p>
                      </td>
                      <td className="py-3 px-4">
                        {row.fileName ? (
                          <p className="font-mono text-[11px] text-slate-600 dark:text-slate-300 truncate max-w-[200px]" title={row.fileName}>
                            {row.fileName}
                          </p>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {row.errorCode ? (
                          <div className="flex flex-col gap-0.5">
                            <code className="font-mono text-[11px] text-rose-600 dark:text-rose-400">{row.errorCode}</code>
                            {row.failedStep && <span className="inline-block w-fit rounded bg-rose-100 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">@ {row.failedStep}</span>}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-mono text-xs tabular-nums ${row.attempts > 1 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500'}`}>{row.attempts}</span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-mono text-[11px] text-slate-600 dark:text-slate-300">{formatDateTime(row.createdAt)}</p>
                      </td>
                      <td className="py-3 px-4 text-right pr-5">
                        <div className="flex items-center justify-end gap-1.5">
                          {row.status === 'failed' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleRetry(row.id);
                              }}
                              disabled={retrying === row.id}
                              className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-rose-700 transition-colors hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300 dark:hover:bg-rose-950/60"
                            >
                              <span className={`material-symbols-outlined text-[12px] ${retrying === row.id ? 'animate-spin' : ''}`}>{retrying === row.id ? 'progress_activity' : 'replay'}</span>
                              {retrying === row.id ? '…' : 'Reintentar'}
                            </button>
                          )}
                          <span className="material-symbols-outlined text-[16px] text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500">chevron_right</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Footer / paginación */}
        {data && data.total > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 font-mono text-[11px] text-slate-500 dark:border-slate-800">
            <p>
              Mostrando <span className="text-slate-700 dark:text-slate-200">{(data.page - 1) * data.pageSize + 1}</span>
              –<span className="text-slate-700 dark:text-slate-200">{Math.min(data.page * data.pageSize, data.total)}</span> de{' '}
              <span className="text-slate-700 dark:text-slate-200 tabular-nums">{data.total}</span>
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-30 dark:border-slate-700 dark:hover:bg-slate-800">
                ← prev
              </button>
              <span className="tabular-nums">
                {page} / {totalPages}
              </span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-30 dark:border-slate-700 dark:hover:bg-slate-800">
                next →
              </button>
            </div>
          </div>
        )}
      </section>

      <ScrapperEventDetailDrawer
        event={selected}
        onClose={() => setSelected(null)}
        onRetry={async (id) => {
          await handleRetry(id);
          setSelected(null);
        }}
        retrying={retrying}
      />
    </>
  );
}
